// Оценка результата человеком. SMY-123 · P8 Э8.5
//
// Зачем она вообще заводится. До неё о недовольстве говорили только косвенные
// признаки: повтор генерации, бесплатная правка, отмена на экране 04,
// обращение в поддержку. Каждый из них — уже потраченные деньги или ушедший
// человек, ни один не отличает «сделали не то» от «сделали то, но плохо», а
// молчаливый уход не оставляет ни одного.
//
// ГЛАВНОЕ РЕШЕНИЕ ФАЙЛА: ОТСУТСТВИЕ ОЦЕНКИ — НЕ НЕЙТРАЛЬНО И НЕ НОЛЬ.
// Поэтому здесь нет ни средней оценки, ни «рейтинга»: складывать двузначное с
// пустотой нельзя. Есть доля довольных ОТ ОЦЕНИВШИХ и рядом с ней — доля
// оценивших от всех. Метрика качества, посчитанная по десяти процентам,
// которые не поленились нажать, говорит о них, а не о продукте, и обязана
// приходить вместе с этим предупреждением, а не отдельно от него.
//
// ПРИВАТНОСТЬ. Оценка и причина из списка — не содержимое: перечисления,
// такие же как в плане событий, и в агрегатах они показываются свободно.
// Свободный текст — содержимое, и он живёт по правилам содержимого:
// открывается отдельным правом, с причиной и записью в журнал (SMY-94).

// --- Что можно поставить ------------------------------------------------------

/**
 * Две кнопки, а не пять звёзд.
 *
 * Пятибалльная шкала выглядит информативнее и таковой не является: разница
 * между «4» и «5» у разных людей означает разное, а решение по ней всё равно
 * принимается одно — разбирать или нет. Две кнопки дают тот же ответ и
 * стоят человеку одно нажатие вместо выбора.
 */
export const RATING_VERDICTS = ['good', 'bad'] as const
export type RatingVerdict = (typeof RATING_VERDICTS)[number]

/**
 * Почему плохо. Список закрытый — по той же причине, что и темы обращений:
 * свободная строка сделала бы долю несчитаемой в тот же день.
 *
 * Спрашивается только у плохой оценки: «почему хорошо» — вопрос, на который
 * человеку нечего ответить, а нам нечего с ответом сделать.
 */
export const RATING_REASONS = ['misunderstood', 'incomplete', 'wrong', 'slow', 'other'] as const
export type RatingReason = (typeof RATING_REASONS)[number]

export const RATING_REASON_LABELS: Record<RatingReason, string> = {
  misunderstood: 'Не то поняли',
  incomplete: 'Неполно',
  wrong: 'Неверно',
  slow: 'Долго',
  other: 'Другое',
}

/**
 * Потолок свободного текста.
 *
 * Не защита от злоупотребления — она в другом месте, — а граница жанра: это
 * поле для «в третьем пункте перепутаны даты», а не для второго запроса.
 * Длинный текст здесь означал бы, что человек пытается продолжить работу
 * через форму отзыва, и правильный ответ на это — не увеличить потолок.
 */
export const RATING_COMMENT_MAX = 1000

// --- Как часто спрашивать -----------------------------------------------------

/**
 * Частота вопроса. Настраивается без релиза (SMY-32), потому что крутить её
 * придётся во время беты: слишком часто — налог на пользование, слишком
 * редко — нечего разбирать.
 */
export interface RatingsConfig {
  /**
   * Спрашивать у каждого N-го завершённого запуска человека.
   *
   * 1 — у каждого. 0 — НЕ СПРАШИВАТЬ ВОВСЕ, и это законное значение, а не
   * забытое поле: во время беты бывает нужно выключить вопрос на неделю
   * ценового теста, чтобы он не путался с ним. То же правило, что у
   * `freeEdits.count`, где ноль означает «правок нет».
   */
  askEveryNth: number
}

/**
 * Спрашивать ли оценку у этого запуска.
 *
 * СЧИТАЕТСЯ ПО ПОРЯДКОВОМУ НОМЕРУ, А НЕ СЛУЧАЙНО. Случайный отбор дал бы
 * человеку два вопроса подряд и потом тишину на месяц; порядковый — ровный
 * шаг, одинаковый у всех. Заодно он воспроизводим: разбирая жалобу «меня
 * спрашивают на каждом шагу», можно посчитать то же самое и увидеть то же.
 *
 * ПЕРВЫЙ ЗАПУСК СПРАШИВАЕТСЯ ВСЕГДА (при включённом вопросе). Он же момент
 * активации — того самого «полезного результата в первые 5 минут», по
 * которому меряется гейт, — и оценка ровно этого результата стоит дороже
 * любой следующей.
 */
export function shouldAskRating(facts: {
  /** Какой по счёту завершённый запуск человека это, начиная с единицы. */
  ordinal: number
  config: RatingsConfig
  /** Уже оценённое не переспрашивается: человек своё уже сказал. */
  rated: boolean
}): boolean {
  if (facts.rated) return false
  const every = facts.config.askEveryNth
  if (!Number.isInteger(every) || every < 1) return false
  if (facts.ordinal < 1) return false
  return (facts.ordinal - 1) % every === 0
}

// --- Как читать оценки --------------------------------------------------------

export interface RatingShare {
  /** Сколько запусков в выборке — знаменатель доли оценивших. */
  runs: number
  rated: number
  good: number
  bad: number
  /**
   * Доля довольных ОТ ОЦЕНИВШИХ. Пусто, когда не оценивал никто: ноль здесь
   * означал бы «всем не понравилось», а это другое утверждение.
   */
  goodShare: number | null
  /**
   * Доля оценивших от всех. Показывается РЯДОМ с `goodShare` всегда, а не по
   * запросу: без неё качество, посчитанное по десяти процентам, читается как
   * качество продукта.
   */
  ratedShare: number | null
}

/** Свести оценки в долю. Чистая функция: её проверяют без базы. */
export function ratingShare(facts: { runs: number; good: number; bad: number }): RatingShare {
  const rated = facts.good + facts.bad
  return {
    runs: facts.runs,
    rated,
    good: facts.good,
    bad: facts.bad,
    goodShare: rated > 0 ? facts.good / rated : null,
    ratedShare: facts.runs > 0 ? rated / facts.runs : null,
  }
}

/**
 * Хватает ли оценок, чтобы вообще о чём-то говорить.
 *
 * Порог тот же по духу, что `minSample` у метрик гейта: на трёх оценках любая
 * доля равна 0, 33, 67 или 100 процентам, и «тему обслуживают плохо» на такой
 * выборке — это шум, принятый за решение.
 */
export const RATING_MIN_SAMPLE = 10

export const ratingIsMeaningful = (share: RatingShare): boolean => share.rated >= RATING_MIN_SAMPLE
